import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";

/**
 * Universal Change-password dialog for every logged-in user (any role, any tenant).
 * - Trigger via the `open`/`onClose` props.
 * - Auto-shows if the user still has must_change_password=true after first login
 *   (mounted at the top of AppShell so every portal picks it up).
 */
export default function ChangePasswordDialog({ open, onClose, mustChange = false }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const validate = () => {
    if (!cur) return "Enter your current password.";
    if (!nw || nw.length < 8) return "New password must be at least 8 characters.";
    if (!/[A-Z]/.test(nw)) return "Include at least one uppercase letter.";
    if (!/[a-z]/.test(nw)) return "Include at least one lowercase letter.";
    if (!/[0-9]/.test(nw)) return "Include at least one digit.";
    if (nw === cur) return "New password must differ from current.";
    if (nw !== confirm) return "Confirmation doesn't match new password.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed. Please use the new password from your next sign-in.");
      setCur(""); setNw(""); setConfirm("");
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally { setSaving(false); }
  };

  const dismiss = () => {
    if (mustChange) {
      toast("You must set a new password before continuing.");
      return;
    }
    setCur(""); setNw(""); setConfirm("");
    onClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-orange-500" /> Change password
          </DialogTitle>
          <DialogDescription>
            {mustChange
              ? "For security, please replace the auto-generated password from your welcome email with something only you know."
              : "Your new password takes effect immediately. Your current session stays signed in."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Current password</Label>
            <div className="relative">
              <Input type={showCur ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" data-testid="cp-current-input" />
              <button type="button" onClick={() => setShowCur((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="cp-current-toggle">
                {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">New password</Label>
            <div className="relative">
              <Input type={showNew ? "text" : "password"} value={nw} onChange={(e) => setNw(e.target.value)} autoComplete="new-password" data-testid="cp-new-input" />
              <button type="button" onClick={() => setShowNew((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="cp-new-toggle">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-[10px] text-muted-foreground">Min 8 chars · at least 1 uppercase, 1 lowercase, 1 digit.</div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Confirm new password</Label>
            <Input type={showNew ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="cp-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          {!mustChange && <Button variant="outline" onClick={dismiss} data-testid="cp-cancel">Cancel</Button>}
          <Button onClick={submit} disabled={saving} data-testid="cp-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Updating</> : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
